// src/components/cards/EmployeeExpandedView.jsx
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab, Chip, Typography } from '@mui/material';
import BaseExpandedCard from './BaseExpandedCard';
import { useEmployeeExpandedData } from '../../hooks/useEmployeeExpandedData';
import { standardizeEmployeeId } from '../../utils/formatters';
import { apiFetch } from '../../utils/api';
import { formatAvailabilityFromObject } from '../../utils/employeeDataProcessing';
import AvailabilityChip from '../common/AvailabilityChip';

// Section components
import EngagementsSection from '../sections/EngagementsSection';
import PersonalInfoSection from '../sections/PersonalInfoSection';
import CompensationFinancialSection from '../sections/CompensationFinancialSection';
import TimeSheetsSection from '../sections/TimeSheetsSection';

// Metric detail modals (rendered in tab mode — no nested dialog)
import DIRDetailsModal from '../../modals/DIRDetailsModal';
import CSOTDetailsModal from '../../modals/CSOTDetailsModal';
import CSATDetailsModal from '../../modals/CSATDetailsModal';
import UtilizationDetailsModal from '../../modals/UtilizationDetailsModal';
import TechnicalSkillsModal from '../../modals/TechnicalSkillsModal';

import { Logger } from '../../utils/logger';

const logger = new Logger('EmployeeExpandedView');

// Tab index constants
export const TABS = {
  DIR: 0,
  CSOT: 1,
  CSAT: 2,
  UTIL: 3,
  SKILLS: 4,
  ENGAGEMENTS: 5,
  COMPENSATION: 6,
  PERSONAL: 7,
  TIMESHEETS: 8,
};

const EmployeeExpandedView = ({ employee, open, onClose, initialTab = 0, onAddToLayout }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  // Track which tabs have ever been shown — once mounted, stay mounted (no remount flash)
  const [mountedTabs, setMountedTabs] = useState(() => new Set([initialTab]));

  // Reset to requested tab whenever the view opens
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setMountedTabs(new Set([initialTab]));
    }
  }, [open, initialTab]);

  const { data, loading, errors } = useEmployeeExpandedData(
    employee?.employee_id,
    employee,
    open
  );

  // When opened via context stub (no title/manager), fetch basic info for the header
  const [basicInfo, setBasicInfo] = useState(null);
  useEffect(() => {
    if (!open || !employee?.employee_id || employee?.title) {
      setBasicInfo(null);
      return;
    }
    const id = standardizeEmployeeId(employee.employee_id);
    if (!id) return;
    apiFetch(`employees/${id}`)
      .then(r => r.ok ? r.json() : null)
      .then(emp => {
        if (emp) {
          setBasicInfo({
            title: emp.title || null,
            manager_name: emp.manager_name || null,
            availability_text: formatAvailabilityFromObject(emp),
          });
        }
      })
      .catch(() => {});
  }, [open, employee?.employee_id, employee?.title]);

  const employeeName = useMemo(() => {
    if (!employee) return 'Employee';
    if (employee.first_name && employee.last_name)
      return `${employee.first_name} ${employee.last_name}`;
    return employee.name || 'Employee';
  }, [employee]);

  const employeeId = useMemo(
    () => standardizeEmployeeId(employee?.employee_id),
    [employee?.employee_id]
  );

  const compensationFinancialData = useMemo(() => ({
    salaryHistory: data.compensation?.salary_history || [],
    totalCompensation: data.compensation?.totalCompensation || {},
    financialSummary: data.compensation?.financial_summary || {},
    compensationProgression: data.compensation?.compensation_progression || {},
  }), [data.compensation]);

  const renderTab = (tab) => {
    switch (tab) {
      case TABS.DIR:
        return <DIRDetailsModal tabMode open={true} onClose={() => {}} employeeName={employeeName} employeeId={employeeId} />;
      case TABS.CSOT:
        return <CSOTDetailsModal tabMode open={true} onClose={() => {}} employeeName={employeeName} employee_id={employeeId} />;
      case TABS.CSAT:
        return <CSATDetailsModal tabMode open={true} onClose={() => {}} employeeName={employeeName} employee_id={employeeId} />;
      case TABS.UTIL:
        return <UtilizationDetailsModal tabMode open={true} onClose={() => {}} employeeName={employeeName} employeeId={employeeId} />;
      case TABS.SKILLS:
        return <TechnicalSkillsModal tabMode open={true} onClose={() => {}} employeeName={employeeName} employeeId={employeeId} />;
      case TABS.ENGAGEMENTS:
        return <EngagementsSection engagementData={data.engagement} loading={loading.engagement} error={errors.engagement} />;
      case TABS.COMPENSATION:
        return (
          <CompensationFinancialSection
            salaryHistory={compensationFinancialData.salaryHistory}
            totalCompensation={compensationFinancialData.totalCompensation}
            financialSummary={compensationFinancialData.financialSummary}
            compensationProgression={compensationFinancialData.compensationProgression}
            employee={{ ...employee, employee_id: employeeId }}
            loading={loading.compensation}
            error={errors.compensation}
          />
        );
      case TABS.PERSONAL:
        return <PersonalInfoSection employee={data.personal} loading={loading.personal} error={errors.personal} />;
      case TABS.TIMESHEETS:
        return <TimeSheetsSection timesheetData={data.timesheet} employeeId={employee?.employee_id} loading={loading.timesheet} error={errors.timesheet} />;
      default:
        return null;
    }
  };

  const TAB_LABELS = [
    'DIR', 'CSOT', 'CSAT', 'Utilization', 'Skills',
    'Engagements', 'Financials', 'Personal Info', 'Time Sheets'
  ];

  const availability = employee?.availability_text || basicInfo?.availability_text;
  const managerName  = employee?.manager_name || basicInfo?.manager_name;
  const jobTitle     = employee?.title || basicInfo?.title;

  const headerMeta = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {jobTitle && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {jobTitle}
        </Typography>
      )}
      {(availability || employee?.confirmed_end) && (
        <AvailabilityChip
          label={(() => {
            const cd = employee?.confirmed_end;
            if (!cd) return availability === 'Now' ? 'Available Now' : `Available ${availability}`;
            const d = new Date(cd + 'T00:00:00');
            const today = new Date(); today.setHours(0,0,0,0);
            return d <= today ? 'Available Now' : `Available ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          })()}
          softEnd={employee?.soft_end || undefined}
        />
      )}
      {managerName && managerName !== 'N/A' && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Manager: {managerName}
        </Typography>
      )}
    </Box>
  );

  return (
    <BaseExpandedCard
      open={open}
      onClose={onClose}
      title={`${employeeName}`}
      headerMeta={headerMeta}
      onAddToLayout={onAddToLayout}
    >
      <Box sx={{ width: '100%' }}>
        {/* Tabs Navigation */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setMountedTabs(prev => new Set([...prev, v]));
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 'medium',
                fontSize: '0.85rem',
                minWidth: 'unset',
                px: 2,
              },
              '& .MuiTab-root.Mui-selected': { color: '#03DAC6' },
              '& .MuiTabs-indicator': { backgroundColor: '#03DAC6' },
            }}
          >
            {TAB_LABELS.map(label => <Tab key={label} label={label} />)}
          </Tabs>
        </Box>

        {/* Tab Panels — lazy mount (first visit), sticky (never unmount after) */}
        <Box>
          {[TABS.DIR, TABS.CSOT, TABS.CSAT, TABS.UTIL, TABS.SKILLS,
            TABS.ENGAGEMENTS, TABS.COMPENSATION, TABS.PERSONAL, TABS.TIMESHEETS
          ].map(tab => (
            <Box key={tab} sx={{ display: activeTab === tab ? 'block' : 'none' }}>
              {mountedTabs.has(tab) && renderTab(tab)}
            </Box>
          ))}
        </Box>
      </Box>
    </BaseExpandedCard>
  );
};

EmployeeExpandedView.propTypes = {
  employee: PropTypes.shape({
    employee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    title: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialTab: PropTypes.number,
  onAddToLayout: PropTypes.func,
};

export default EmployeeExpandedView;
